import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "./RootStore";
import * as Services from "../Service/Services";
import { getDocumentMetadata } from "../Service/Services";
import {
  Document,
  DocumentType,
  FormSchema,
  FormSchemaJSON,
  DocumentFormData,
  ODataResponse,
  OptionItem,
  RecycleBinItem,
  RegionalTransfer,
} from "../Components/Documents/ViewDocs/Documents.types";
import { config } from "../Utility/Constants";
import {
  getLocalDateString,
  getQualifyingRegionalTransfer,
  getStateCode,
} from "../Utility/Utility";

/**
 * DocumentStore - Domain store for Documents module
 * Manages document data, document types, form schemas, and document operations
 */
export class DocumentStore {
  rootStore: RootStore;

  // Observable state
  documents: Document[] = [];
  selectedDocument: Document | null = null;
  isLoading: boolean = false;
  documentTypes: DocumentType[] = [];
  isUploading: boolean = false;
  currentFormSchema: FormSchema | null = null;
  uploadProgress: number = 0; // Upload progress percentage (0-100)
  uploadedBytes: number = 0; // Bytes uploaded so far
  totalBytes: number = 0; // Total file size in bytes
  setupLists: Record<string, OptionItem[]> = {}; // Dynamic option lists loaded from SharePoint
  recycleBinItems: RecycleBinItem[] = [];
  deletedDocumentsCache = new Map<string, Document>();
  isRecycleBinLoading: boolean = false;
  isRestoring: boolean = false;
  regionalTransfer: RegionalTransfer | null = null; // Approved future-dated transfer, null = no region toggle
  isNewRegionSelected: boolean = false; // Region toggle: false = Current (default), true = New
  isRegionalTransferLoading: boolean = true; // Documents page shows its spinner until the transfer check finishes

  // Request counters so a slower, superseded response never overwrites a newer one
  private documentTypesRequestId = 0;
  private regionalTransferRequestId = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  /**
   * Region used to load Upload Document types - the transfer's new region when
   * the New toggle is selected, otherwise the user's profile region
   */
  get selectedRegion(): string {
    if (this.isNewRegionSelected && this.regionalTransfer) {
      return this.regionalTransfer.newRegion;
    }
    return this.rootStore.userStore.user?.Region ?? "";
  }

  /**
   * State sent to the credentialing system after upload - the transfer's new state
   * when the New toggle is selected, otherwise the user's profile state
   */
  get selectedState(): string {
    if (this.isNewRegionSelected && this.regionalTransfer) {
      return this.regionalTransfer.newStateCode;
    }
    return this.rootStore.userStore.user?.State ?? "";
  }

  /**
   * Load the current user's approved, future-dated regional transfer (if any)
   * Always resets the toggle to Current. Failures are logged only - the page
   * simply shows no toggle and keeps its standard behaviour.
   */
  fetchRegionalTransfer = async () => {
    const requestId = ++this.regionalTransferRequestId;
    this.regionalTransfer = null;
    this.isNewRegionSelected = false;
    this.isRegionalTransferLoading = true;

    const fullName = this.rootStore.userStore.user?.FullName;
    if (!fullName) {
      this.isRegionalTransferLoading = false;
      return;
    }

    try {
      const response = await Services.getUserRegionalTransfers(fullName);
      const rawItems = Array.isArray(response?.data?.items)
        ? response.data.items
        : Array.isArray(response?.data?.value)
          ? response.data.value
          : [];

      const transfer = getQualifyingRegionalTransfer(
        rawItems,
        getLocalDateString(),
      );

      runInAction(() => {
        if (requestId !== this.regionalTransferRequestId) return;
        this.regionalTransfer = transfer;
        this.isRegionalTransferLoading = false;
      });

      // The state code is only needed at upload time, so resolve it after the
      // banner and Upload Document button are shown rather than delaying them
      if (transfer) {
        const newStateCode = await this.resolveStateCode(transfer.newState);
        runInAction(() => {
          if (requestId !== this.regionalTransferRequestId) return;
          if (this.regionalTransfer) {
            this.regionalTransfer.newStateCode = newStateCode;
          }
        });
      }
    } catch (error: unknown) {
      console.error("Failed to load regional transfers:", error);
      runInAction(() => {
        if (requestId !== this.regionalTransferRequestId) return;
        this.isRegionalTransferLoading = false;
      });
    }
  };

  /**
   * Clear transfer state when leaving the Documents page and discard any in-flight
   * lookup, so the next visit starts hidden instead of showing stale results
   */
  resetRegionalTransfer = () => {
    this.regionalTransferRequestId++;
    this.regionalTransfer = null;
    this.isNewRegionSelected = false;
    this.isRegionalTransferLoading = true;
  };

  /**
   * Convert a state name (e.g., "California") to the 2-letter code the credentialing
   * API requires. A failed lookup still lets the banner show; the code is just "".
   * @param state - State name or code from the transfer record
   */
  private resolveStateCode = async (state: string): Promise<string> => {
    const code = getStateCode([], state);
    if (code || !state) return code;

    try {
      const response = await Services.getStates();
      const rows = Array.isArray(response?.data)
        ? response.data
        : (response?.data?.items ?? response?.data?.value ?? []);
      const resolved = getStateCode(rows, state);

      if (!resolved) {
        console.warn(`Could not resolve a state code for "${state}".`);
      }
      return resolved;
    } catch (error: unknown) {
      console.error("Failed to load states:", error);
      return "";
    }
  };

  /**
   * Switch the region toggle between Current (false) and New (true)
   * @param value - true to use the transfer's new region
   */
  setNewRegionSelected = (value: boolean) => {
    this.isNewRegionSelected = value && !!this.regionalTransfer;
  };

  /**
   * Fetch documents for the current user from UserStore
   */
  fetchDocumentsForCurrentUser = async () => {
    if (
      !this.rootStore.userStore.isAuthenticated ||
      !this.rootStore.userStore.user?.LoginName
    ) {
      const errorMessage =
        "Cannot fetch documents: No authenticated user found.";

      this.rootStore.uiStore.setError(errorMessage);
      return;
    }

    const userName = this.rootStore.userStore.formattedUserName;

    await this.fetchDocuments(userName);
  };

  /**
   * Fetch documents for a user (private method)
   * @param userName - User login name in Windows NT format (e.g., domain\username)
   */
  private fetchDocuments = async (userName: string) => {
    this.isLoading = true;

    try {
      // const response = await Services.getDocuments(userName);
      // const data = response.data as ODataResponse<Document>;
      // STEP 1: Fetch the first page
      const firstResponse = await Services.getDocuments(userName);
      let data = firstResponse.data as ODataResponse<Document>;
      const allDocuments: Document[] = [...(data.value || [])];
      // STEP 2: Follow @odata.nextLink pages until exhausted
      // Safety cap: 50 pages × 100 per page = 5,000 documents max, guards against infinite loops
      const MAX_PAGES = 50;
      let pageCount = 1;
      let pagingError: unknown = null;

      try {
        while (data["@odata.nextLink"] && pageCount < MAX_PAGES) {
          const nextResponse = await Services.getDocumentsByNextLink(
            data["@odata.nextLink"],
          );
          data = nextResponse.data as ODataResponse<Document>;
          allDocuments.push(...(data.value || []));
          pageCount++;
        }
      } catch (error: unknown) {
        // A failure part-way through paging must not discard the pages already
        // retrieved. Letting it escape rendered the page as "No documents found"
        // even though the first page had loaded successfully (Task 9813).
        pagingError = error;
      }

      runInAction(() => {
        this.documents = allDocuments;
        this.isLoading = false;
      });

      if (pagingError) {
        this.rootStore.uiStore.setError(
          `Only ${allDocuments.length} document(s) could be loaded. Some documents may be missing - please refresh to try again.`,
        );
      }
    } catch (error: unknown) {
      runInAction(() => {
        this.isLoading = false;
      });

      const errorMessage =
        error instanceof Error ? error.message : "Failed to load documents.";

      this.rootStore.uiStore.setError(errorMessage);
    }
  };

  /**
   * Fetch document types filtered by role and region
   * @param role - Employee role (e.g., "Senior Analyst")
   * @param region - Employee region (e.g., "LA Metro")
   */
  fetchDocumentTypes = async (role: string, region: string) => {
    const requestId = ++this.documentTypesRequestId;

    try {
      const response = await Services.getDocumentTypes(role, region);
      const data = response.data as ODataResponse<DocumentType>;

      runInAction(() => {
        // Ignore a response for a region the user has since switched away from
        if (requestId !== this.documentTypesRequestId) return;
        this.documentTypes = data.value || [];
      });
    } catch (error: unknown) {
      if (requestId !== this.documentTypesRequestId) return;

      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to load document types.";

      this.rootStore.uiStore.setError(errorMessage);
    }
  };

  /**
   * Fetch document form template and parse FormJSON
   * @param id - Document type ID (must be > 0)
   */
  fetchDocumentFormTemplate = async (id: number) => {
    if (id <= 0) {
      this.rootStore.uiStore.setError("Invalid document type ID.");
      return;
    }

    try {
      const response = await Services.getDocumentFormTemplate(id);
      const data = response.data as ODataResponse<{
        ID: number;
        FormJSON: string;
      }>;

      // Handle both single object and array responses
      const formTemplate = Array.isArray(data.value)
        ? data.value[0]
        : data.value;

      // Check if form template exists
      if (!formTemplate) {
        const errorMessage = `No form template found for document type ID ${id}. Please contact your administrator.`;

        throw new Error(errorMessage);
      }

      // Check if FormJSON is null or empty
      if (!formTemplate.FormJSON || formTemplate.FormJSON.trim().length === 0) {
        const errorMessage = `There are no fields available for this document type.`;

        throw new Error(errorMessage);
      }

      if (formTemplate?.FormJSON) {
        const parsedSchema = JSON.parse(
          formTemplate.FormJSON,
        ) as FormSchemaJSON;

        // Transform fields: map 'id' to 'name' and preserve all properties
        const transformedFields =
          parsedSchema.fields?.map((field: FormSchemaJSON["fields"][0]) => ({
            name: field.id, // Use 'id' as 'name' (field identifier)
            title: field.title, // Preserve 'title' for display label
            type: field.type,
            required: field.required || false,
            options: field.options || field.optionsList,
            defaultValue: field.defaultValue, // For non-Internal fields
            value: field.type === "Internal" ? field.value : undefined, // For Internal fields: preserve value (may contain placeholders)
            copy: field.copy, // For Internal fields: copy from another field
            digitonly: field.digitonly, // For Text fields: strip non-digits
            prefix: field.prefix, // For Text/Note fields: prefix to prepend
            defaultChecked: field.defaultChecked, // For Boolean fields: default checked value
            default: field.default, // For List fields: default value
            multi: field.multi, // For List fields: multi-select flag
            optionsList: (() => {
              // Handle both array and string types (runtime type checking)
              if (
                Array.isArray(field.optionsList) &&
                field.optionsList.length > 0
              ) {
                return field.optionsList[0]; // Dynamic option list name (take first item if array)
              }
              // Type assertion needed because TypeScript thinks optionsList is always string[]
              // but runtime JSON might have a string value
              const optionsListValue = field.optionsList as unknown;
              if (
                typeof optionsListValue === "string" &&
                optionsListValue.trim().length > 0
              ) {
                return optionsListValue.trim(); // Use string directly if it's a valid string
              }
              return undefined; // Set to undefined if invalid
            })(),
            regex: field.regex, // Array of regex validation patterns (OR logic)
            validateAPI: field.validateAPI, // API endpoint for server-side validation
            description: field.description, // Help text displayed below the field
            placeholder: field.placeholder, // Placeholder text for input fields
            error: field.error, // Custom error message for validation
            maxlength: field.maxlength, // Maximum length for text fields
          })) || [];

        const formSchema: FormSchema = {
          type: "document", // Default type
          site:
            typeof parsedSchema.site === "string"
              ? parsedSchema.site
              : parsedSchema.site?.name || "",
          library: parsedSchema.library || "",
          fields: transformedFields,
          maxFiles: 1, // Default single file upload
          fileTypes: parsedSchema.fileTypes || "",
        };

        runInAction(() => {
          this.currentFormSchema = formSchema;
        });
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to load form template.";
      this.rootStore.uiStore.setError(errorMessage);

      // Re-throw the error so the component can catch and display it locally
      throw error;
    }
  };

  /**
   * Clear the current form schema
   */
  clearFormSchema = () => {
    this.currentFormSchema = null;
  };

  /**
   * Load a setup list from SharePoint by name
   * Maps optionsList field values to SharePoint list names
   * @param listName - The setup list name or field identifier (e.g., 'Background Types', 'backgroundTypeLevelTwoBackground')
   * @param siteUrl - Optional SharePoint site URL (defaults to config.url.SHAREPOINT_SITE_URL if not provided)
   */
  loadSetupList = async (listName: string, siteUrl?: string) => {
    // Validate list name - skip if invalid
    if (
      !listName ||
      typeof listName !== "string" ||
      listName.trim().length === 0
    ) {
      return;
    }

    const trimmedListName = listName.trim();

    // Skip if already loaded
    if (this.setupLists[trimmedListName]) {
      return;
    }

    // Get site URL from form schema if available, otherwise use default
    // FormSchema.site is always a string (transformed from FormSchemaJSON)
    const effectiveSiteUrl =
      siteUrl || this.currentFormSchema?.site || config.url.SHAREPOINT_SITE_URL;

    try {
      const options = await Services.getSetupListOptions(
        trimmedListName,
        effectiveSiteUrl,
      );
      runInAction(() => {
        this.setupLists[trimmedListName] = options;
      });
    } catch (error: unknown) {
      // Error is already logged in getSetupListOptions with improved message
      // Don't show UI error for setup list failures - just log
    }
  };

  /**
   * Upload document with metadata to SharePoint
   * @param file - File to upload
   * @param siteUrl - SharePoint site URL
   * @param libraryName - Document library name
   * @param folderPath - Subfolder path
   * @param metadata - Metadata fields as key-value pairs
   * @param overwrite - Whether to overwrite existing file (default: false)
   * @param user - User identifier for Author/Editor fields (email, DOMAIN\username, or SPID)
   * @param userState - User's state code for credentialing system (e.g., "FL", "CO")
   */
  uploadDocument = async (
    file: File,
    siteUrl: string,
    libraryName: string,
    folderPath: string,
    metadata: DocumentFormData,
    overwrite: boolean = false,
    user?: string,
    userState?: string,
  ) => {
    // Initialize upload progress state
    runInAction(() => {
      this.isUploading = true;
      this.uploadProgress = 0;
      this.uploadedBytes = 0;
      this.totalBytes = file.size;
    });

    try {
      await Services.uploadDocument(
        file,
        siteUrl,
        libraryName,
        folderPath,
        metadata,
        overwrite,
        user,
        (progressEvent) => {
          // Progress callback updates store observables
          runInAction(() => {
            this.uploadedBytes = progressEvent.loaded;
            this.totalBytes = progressEvent.total;
            this.uploadProgress =
              progressEvent.total > 0
                ? Math.round((progressEvent.loaded / progressEvent.total) * 100)
                : 0;
          });
        },
      );

      this.rootStore.uiStore.closeUploadDialog();
      this.rootStore.uiStore.setSuccessMessage(
        "Document uploaded successfully.",
      );

      runInAction(() => {
        this.isUploading = false;
        this.resetUploadProgress();
      });

      // Update credentialing system after refresh
      await this.updateCredentialingSystem(metadata, userState);
    } catch (error: unknown) {
      runInAction(() => {
        this.isUploading = false;
        this.resetUploadProgress();
      });

      const errorMessage =
        error instanceof Error ? error.message : "Failed to upload document.";

      this.rootStore.uiStore.setError(errorMessage);
    }
  };

  /**
   * Reset upload progress state after upload completes or fails
   */
  resetUploadProgress = () => {
    this.uploadProgress = 0;
    this.uploadedBytes = 0;
    this.totalBytes = 0;
  };

  /**
   * Update document metadata in SharePoint
   * @param fileName - Name of the file to update
   * @param siteUrl - SharePoint site URL
   * @param libraryName - Document library name
   * @param folderPath - Subfolder path
   * @param metadata - Metadata fields to update
   */
  updateDocumentMetadata = async (
    fileName: string,
    siteUrl: string,
    libraryName: string,
    folderPath: string,
    metadata: DocumentFormData,
  ) => {
    try {
      await Services.updateDocumentMetadata(
        fileName,
        siteUrl,
        libraryName,
        folderPath,
        metadata,
      );

      this.rootStore.uiStore.setSuccessMessage(
        "Document metadata updated successfully.",
      );
      this.rootStore.uiStore.closeEditPanel();

      // Refresh documents list
      // Note: userName will need to be passed from component context
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to update document metadata.";

      this.rootStore.uiStore.setError(errorMessage);
    }
  };

  /**
   * Delete document from SharePoint
   * @param fileName - Name of the file to delete
   * @param siteUrl - SharePoint site URL
   * @param libraryName - Document library name
   * @param folderPath - Subfolder path (optional)
   */
  deleteDocument = async (
    fileName: string,
    siteUrl: string,
    libraryName: string,
    folderPath?: string,
  ) => {
    try {
      // Find the document to cache before removing it
      const docToCache = this.documents.find((doc) => doc.FileName === fileName);

      await Services.deleteDocument(fileName, siteUrl, libraryName, folderPath);

      runInAction(() => {
        if (docToCache) {
          this.deletedDocumentsCache.set(fileName, docToCache);
        }
        // Remove document from local state
        this.documents = this.documents.filter(
          (doc) => doc.FileName !== fileName,
        );
        if (this.selectedDocument?.FileName === fileName) {
          this.selectedDocument = null;
        }
      });

      this.rootStore.uiStore.closeDeleteDialog();
      this.rootStore.uiStore.setSuccessMessage(
        "Document deleted successfully.",
      );

      // Wait 3 seconds, then refresh the active document listing to ensure synchronized state
      setTimeout(async () => {
        try {
          await this.fetchDocumentsForCurrentUser();
        } catch (err) {
          console.error("Failed to refresh documents after delete:", err);
        }
      }, 3000);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete document.";

      this.rootStore.uiStore.setError(errorMessage);
    }
  };

  /**
   * Select a document for viewing/editing
   * @param document - Document to select
   */
  selectDocument = (document: Document | null) => {
    this.selectedDocument = document;
  };

  /**
   * Clear selected document
   */
  clearSelectedDocument = () => {
    this.selectedDocument = null;
  };

  /**
   * Update credentialing system after document upload
   * Removes document type from user's missing items list
   * @param metadata - Upload metadata containing ContractorMasterDocumentIDId
   * @param userState - User's state code (optional, defaults to 'FL' if not provided)
   */
  private updateCredentialingSystem = async (
    metadata: DocumentFormData,
    userState?: string,
  ) => {
    // Only update if ContractorMasterDocumentIDId is present
    const documentTypeId = metadata.ContractorMasterDocumentIDId as
      | number
      | undefined;
    if (!documentTypeId) {
      return;
    }

    try {
      // Get document type title from loaded documentTypes
      const documentType = this.documentTypes.find(
        (dt) => dt.ID === documentTypeId,
      );
      if (!documentType?.Title) {
        return;
      }

      // Use provided state or fallback to default
      const state = userState || "";

      await Services.removeOwnMissingItem(documentType.Title, state);
    } catch (error: unknown) {}
  };

  /**
   * Fetch full document list item metadata from SharePoint
   * Used by DocumentDetailPanel to load all custom columns
   */
  fetchDocumentListItemMetadata = async (
    siteUrl: string,
    libraryName: string,
    listItemId: number,
  ): Promise<Record<string, any> | null> => {
    try {
      const response = await getDocumentMetadata(
        String(listItemId),
        siteUrl,
        libraryName,
      );

      // Extract fieldValues from response
      const metadata = response.data?.fieldValues || response.data || {};

      return metadata;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      console.error(errorMessage);
      throw error;
    }
  };

  /**
   * Computed: Group documents by ContentType
   */
  get groupedDocuments(): Record<string, Document[]> {
    const grouped: Record<string, Document[]> = {};

    this.documents.forEach((doc) => {
      const contentType = doc.ContentType || "Other";
      if (!grouped[contentType]) {
        grouped[contentType] = [];
      }
      grouped[contentType].push(doc);
    });

    return grouped;
  }

  /**
   * Computed: Total document count
   */
  get documentCount(): number {
    return this.documents.length;
  }

  /**
   * Computed: Check if documents exist
   */
  get hasDocuments(): boolean {
    return this.documents.length > 0;
  }

  /**
   * Fetch Recycle Bin items from SharePoint
   */
  fetchRecycleBinItems = async () => {
    this.isRecycleBinLoading = true;
    this.rootStore.uiStore.clearError();

    try {
      const siteUrl = config.url.SHAREPOINT_SITE_URL;
      const response = await Services.getRecycleBinItems(siteUrl);
      const items = response.data as RecycleBinItem[];

      runInAction(() => {
        this.recycleBinItems = items || [];
        this.isRecycleBinLoading = false;
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.isRecycleBinLoading = false;
      });
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to load recycle bin items.";
      this.rootStore.uiStore.setError(errorMessage);
    }
  };

  /**
   * Restore an item from the Recycle Bin
   * @param itemId - Unique GUID of the item
   * @param fileName - Original leafName of the item
   */
  restoreRecycleBinItem = async (itemId: string, fileName: string) => {
    this.isRestoring = true;
    this.rootStore.uiStore.clearError();
    this.rootStore.uiStore.clearSuccessMessage();

    try {
      const siteUrl = config.url.SHAREPOINT_SITE_URL;
      await Services.restoreRecycleBinItem(itemId, siteUrl);

      runInAction(() => {
        // Remove item from local recycle bin items
        this.recycleBinItems = this.recycleBinItems.filter(
          (item) => item.id !== itemId,
        );

        // Retrieve full document details from cache if present
        const cachedDoc = this.deletedDocumentsCache.get(fileName);
        if (cachedDoc) {
          // Push back to the documents array so it instantly reappears in the listing screen
          this.documents.push(cachedDoc);
          // Delete from cache
          this.deletedDocumentsCache.delete(fileName);
        }

        this.isRestoring = false;
      });

      this.rootStore.uiStore.setSuccessMessage(
        "Document restored successfully.",
      );

      // Wait 3 seconds, then refresh the active document listing to ensure synchronized state
      setTimeout(async () => {
        try {
          await this.fetchDocumentsForCurrentUser();
        } catch (err) {
          console.error("Failed to refresh documents after restore:", err);
        }
      }, 3000);
    } catch (error: unknown) {
      runInAction(() => {
        this.isRestoring = false;
      });
      const errorMessage =
        error instanceof Error ? error.message : "Failed to restore document.";
      this.rootStore.uiStore.setError(errorMessage);
    }
  };

  /**
   * Computed getter to return Recycle Bin items.
   * Note: The backend API already handles secure database-backed user isolation.
   */
  get filteredRecycleBinItems(): RecycleBinItem[] {
    return this.recycleBinItems;
  }
}
